import React, { useState, useEffect, useCallback, useRef } from 'react';
import classnames from 'classnames/bind';
import { Lightbulb } from 'lucide-react';

import BaseModal from '@MODALS/BaseModal';

import styles from './DocumentStructureHelpModal.module.scss';

const cx: CX = classnames.bind(styles);

interface DocumentStructureHelpModalProps {
    isOpen: boolean;
    onClose: () => void;
}

function DocumentStructureHelpModal({ isOpen, onClose }: DocumentStructureHelpModalProps) {
    const [animationStep, setAnimationStep] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const modalContentRef = useRef<HTMLDivElement>(null);

    const startAnimation = useCallback(() => {
        setIsPlaying(true);
        setAnimationStep(1);
        
        // Animation sequence with slower, more deliberate timing
        setTimeout(() => setAnimationStep(2), 1200); // Chapter appears
        setTimeout(() => setAnimationStep(3), 2400); // Section title appears
        setTimeout(() => setAnimationStep(4), 3600); // Base text appears
        setTimeout(() => setAnimationStep(5), 4800); // Default variant appears
        setTimeout(() => setAnimationStep(6), 6500); // Check for specific variant (longer pause)
        setTimeout(() => setAnimationStep(7), 11000); // Replace with specific variant (much longer pause)
        setTimeout(() => {
            setIsPlaying(false);
            setAnimationStep(0);
        }, 15000); // Final pause to appreciate the result
    }, []);

    // Auto-scroll function to follow the animation
    const scrollToStep = useCallback((step: number) => {
        if (!modalContentRef.current) return;

        const container = modalContentRef.current;
        const { clientHeight: containerHeight, scrollHeight } = container;
        
        // Only scroll if content is taller than container AND window height is small
        const windowHeight = window.innerHeight;
        const shouldAutoScroll = scrollHeight > containerHeight && windowHeight < 1000;
        
        if (!shouldAutoScroll) return;

        let scrollPosition = 0;
        
        switch (step) {
            case 2: // Chapter appears - stay at top
                scrollPosition = 0;
                break;
            case 3: // Section title appears - scroll down slightly
                scrollPosition = scrollHeight * 0.15;
                break;
            case 4: // Base text appears - scroll to middle
                scrollPosition = scrollHeight * 0.4;
                break;
            case 5: // Default variant appears - scroll down more
                scrollPosition = scrollHeight * 0.6;
                break;
            case 6: // Check for specific variant - scroll to variants area
                scrollPosition = scrollHeight * 0.7;
                break;
            case 7: // Replace with specific variant - scroll to bottom area
                scrollPosition = scrollHeight * 0.8;
                break;
            default:
                return;
        }

        // Smooth scroll to the calculated position
        container.scrollTo({
            top: Math.min(scrollPosition, scrollHeight - containerHeight),
            behavior: 'smooth'
        });
    }, []);

    // Watch animation step changes and trigger scrolling
    useEffect(() => {
        if (animationStep >= 2) {
            // Add a small delay to ensure the content has rendered before scrolling
            setTimeout(() => {
                scrollToStep(animationStep);
            }, 300);
        }
    }, [animationStep, scrollToStep]);

    useEffect(() => {
        if (isOpen) {
            setAnimationStep(0);
            setIsPlaying(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && !isPlaying && animationStep === 0) {
            // Auto-start animation after a longer delay to let users read the intro
            const timer = setTimeout(() => {
                startAnimation();
            }, 1500);
            
            return () => clearTimeout(timer);
        }
    }, [isOpen, isPlaying, animationStep, startAnimation]);

    if (!isOpen) return null;

    return (
        <BaseModal
            onClose={onClose}
            style={{
                width: 'calc(100% - 60px)',
                maxWidth: '1200px',
                height: 'calc(100vh - 40px)',
                maxHeight: 'none'
            }}
        >
            <div className={cx('document-structure-help-modal')}>
                <div className={cx('modal-content')} ref={modalContentRef}>
                    <div className={cx('modal-header')}>
                        <h2 className={cx('modal-title')}>How your sections are built in generated documents</h2>
                        <button
                            type="button"
                            className={cx('close-button')}
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            ×
                        </button>
                    </div>
                    
                    <div className={cx('explanation-text')}>
                        <p>
                            This visualization shows how your section is computed in generated pdf documents from base text and variants.
                        </p>
                    </div>

                    <div className={cx('animation-container')}>
                        <div className={cx('pdf-document')}>
                            <div className={cx('pdf-header')}>
                                <div className={cx('pdf-icon')}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#dc2626" />
                                        <polyline points="14,2 14,8 20,8" fill="#dc2626" />
                                        <text x="12" y="16" textAnchor="middle" fontSize="6" fill="white" fontWeight="bold">PDF</text>
                                    </svg>
                                </div>
                                <div className={cx('pdf-title')}>Generated Document Example</div>
                                <div className={cx('pdf-pages')}>Page 1 of 1</div>
                            </div>
                            
                            <div className={cx('pdf-content')}>
                                {/* Chapter Section */}
                                <div className={cx('content-block', 'chapter-block', {
                                    'animate-in': animationStep >= 2
                                })}
                                >
                                    <div className={cx('content-header')}>
                                        <div className={cx('content-number')}>1.</div>
                                        <div className={cx('content-label')}>Chapter Title</div>
                                    </div>
                                    <div className={cx('content-text', 'chapter-text')}>
                                        <div className={cx('skeleton-line', 'title-line')} />
                                    </div>
                                </div>

                                {/* Section Title */}
                                <div className={cx('content-block', 'section-title-block', 'indented', {
                                    'animate-in': animationStep >= 3
                                })}
                                >
                                    <div className={cx('content-header')}>
                                        <div className={cx('content-number')}>1.1</div>
                                        <div className={cx('content-label')}>Section Title</div>
                                    </div>
                                    <div className={cx('content-text', 'section-title-text')}>
                                        <div className={cx('skeleton-line', 'subtitle-line')} />
                                    </div>
                                </div>

                                {/* Section Base Text */}
                                <div className={cx('content-block', 'base-text-block', 'double-indented', {
                                    'animate-in': animationStep >= 4
                                })}
                                >
                                    <div className={cx('content-header')}>
                                        <div className={cx('content-label', 'highlight')}>Section Base Text</div>
                                    </div>
                                    <div className={cx('content-text', 'base-text')}>
                                        <div className={cx('skeleton-line')} />
                                        <div className={cx('skeleton-line', 'short')} />
                                    </div>
                                    <div className={cx('always-printed')}>✓ Always printed into the generated documents</div>
                                </div>

                                {/* Variant Section */}
                                <div className={cx('variant-container', 'double-indented')}>
                                    {/* Default Variant */}
                                    <div className={cx('content-block', 'variant-block', 'default-variant', {
                                        'animate-in': animationStep >= 5,
                                        'fade-out': animationStep >= 7
                                    })}
                                    >
                                        <div className={cx('content-header')}>
                                            <div className={cx('content-label')}>Default Variant</div>
                                        </div>
                                        <div className={cx('content-text', 'variant-text')}>
                                            <div className={cx('skeleton-line')} />
                                            <div className={cx('skeleton-line', 'short')} />
                                        </div>
                                        <div className={cx('variant-status')}>Checking for more specific variants...</div>
                                    </div>

                                    {/* Replacement Message */}
                                    <div className={cx('replacement-message', {
                                        show: animationStep >= 6 && animationStep < 7
                                    })}
                                    >
                                        <div className={cx('replacement-icon')}>⬆</div>
                                        <div className={cx('replacement-text')}>System now looking for a more specific variant for the use case (e.g. region-specific text)...</div>
                                    </div>

                                    {/* Specific Variant */}
                                    <div className={cx('content-block', 'variant-block', 'specific-variant', {
                                        'animate-in': animationStep >= 7
                                    })}
                                    >
                                        <div className={cx('content-header')}>
                                            <div className={cx('content-label', 'highlight')}>More specific variant found & applied - default variant replaced!</div>
                                        </div>
                                        <div className={cx('content-text', 'variant-text')}>
                                            <div className={cx('skeleton-line')} />
                                            <div className={cx('skeleton-line')} />
                                            <div className={cx('skeleton-line', 'short')} />
                                        </div>
                                        <div className={cx('variant-status', 'success')}>✓ This variant matches document parameters</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={cx('explanation-footer')}>
                        <div className={cx('key-points')}>
                            <div className={cx('points-grid')}>
                                <div className={cx('point-box')}>
                                    <Lightbulb className={cx('point-icon')} size={16} />
                                    <div className={cx('point-text')}>
                                        <strong>1. Section Base Text</strong> always appears first and is always printed
                                    </div>
                                </div>
                                <div className={cx('point-box')}>
                                    <Lightbulb className={cx('point-icon')} size={16} />
                                    <div className={cx('point-text')}>
                                        <strong>2. Default Variants</strong> print unless a more specific variant is found
                                    </div>
                                </div>
                                <div className={cx('point-box')}>
                                    <Lightbulb className={cx('point-icon')} size={16} />
                                    <div className={cx('point-text')}>
                                        <strong>3. Specific Variants</strong> override default variants when conditions match
                                    </div>
                                </div>
                                <div className={cx('point-box')}>
                                    <Lightbulb className={cx('point-icon')} size={16} />
                                    <div className={cx('point-text')}>
                                        4. This allows you to build a rule-based document structure.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </BaseModal>
    );
}

export default DocumentStructureHelpModal; 